import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { displayPrice } from '@/wabunka-src/features/Experience/models/experience';
import { displayDestinationTagName } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTag';

import type { DurationObject } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';
import type { MicroCMSImage } from 'microcms-js-sdk';

/**
 * microCMS `en-experiences` から画像突合に使う最小フィールド。
 * `id` は体験スラグとして使われ、バックエンド体験の `slug` と突合する。
 * PC=`pcMainImage` / SP=`spMainImage` をヒーロー画像、`subImages` を追加ギャラリー画像として扱う。
 * PC のスライドショーは `pcMainImage` ＋ `subImages` を順に回す。
 */
export interface EnExperienceImageContent {
  id: string;
  pcMainImage?: MicroCMSImage;
  spMainImage?: MicroCMSImage;
  /** 追加ギャラリー画像（体験詳細と同じ subImages）。PC スライドショーに使う */
  subImages?: { image?: MicroCMSImage }[];
}

/** Experiential Stays カードのメタ表示（エリア / 日程 / 価格） */
export interface ExperientialStayCardMeta {
  /** エリア名（空文字の場合は非表示） */
  destination: string;
  /** 所要時間表記。他カード（ExperienceCardContent）と同じく `{value}{unit}`（例: "1night"）。空文字の場合は非表示 */
  duration: string;
  /** 価格表記。共通 `displayPrice` と同じ（例: "from ¥150,000 /person"）。空文字の場合は非表示 */
  price: string;
  /** いずれかのメタが存在するか */
  hasMeta: boolean;
}

/** Experiential Stays セクションの表示用カードビューモデル */
export interface ExperientialStayCardViewModel {
  /** 体験スラグ（CMS 突合キー・GA イベント id 兼用） */
  slug: string;
  /** 体験名 */
  titleEn: string;
  /** 表示メタ（エリア / 日程 / 価格） */
  meta: ExperientialStayCardMeta;
  /** 体験詳細ページへのリンク */
  detailLink: string;
  /** CMS 画像（横長） */
  backgroundWideMedias: MicroCMSImage[];
  /** CMS 画像（縦長） */
  backgroundTallMedias: MicroCMSImage[];
}

export type ExperientialStayCardListViewModel = ExperientialStayCardViewModel[];

/**
 * 体験の所要時間を、他セクションの体験カード（ExperienceCardContent）と同じ `{value}{unit}` 形式で表記する。
 * （例: `{ value: 1, unit: 'night' }` → "1night"）。value が 0 以下 / 未設定のときは空文字で日程欄を非表示にする。
 */
export const buildExperientialStayDuration = (duration: DurationObject | undefined): string => {
  if (!duration || duration.value <= 0) return '';
  return `${duration.value}${duration.unit}`;
};

/** en-experiences 画像コンテンツを id（= 体験スラグ）でひけるマップに変換する */
const buildImageMapBySlug = (cmsImages: EnExperienceImageContent[]): Map<string, EnExperienceImageContent> => {
  return new Map(cmsImages.map((image) => [image.id, image]));
};

/** url を持つ MicroCMSImage を 1 枚配列に詰める（未設定は空配列） */
const toMediaList = (image: MicroCMSImage | undefined): MicroCMSImage[] => {
  return image?.url ? [image] : [];
};

/**
 * PC スライドショー用の横長メディア列を組み立てる。
 * 先頭に pcMainImage（ヒーロー）、続いて subImages を url のあるものだけ並べる。
 */
const buildWideMedias = (cms: EnExperienceImageContent): MicroCMSImage[] => {
  const medias = toMediaList(cms.pcMainImage);
  for (const sub of cms.subImages ?? []) {
    if (sub.image?.url) medias.push(sub.image);
  }
  return medias;
};

/**
 * バックエンドの Curated_Stays 体験リストと CMS（en-experiences）画像をスラグで突合し、表示用カードリストを組み立てる。
 * - 並び順はバックエンドの experiences 配列順を維持する
 * - 体験 slug に一致する CMS コンテンツが無い、または PC/SP いずれの画像も無いカードは除外する（突合漏れは詰める）
 * - PC ヒーロー画像 = `pcMainImage`（wide）、SP ヒーロー画像 = `spMainImage`（tall）に詰め替える
 * - メタ（場所 / 日程 / 価格）は他セクションの体験カードと同じロジック（`displayDestinationTagName` / `{value}{unit}` / `displayPrice`）で組み立てる
 *
 * @param experiences バックエンド Curated_Stays セクションの体験リスト（順序付き）
 * @param cmsImages microCMS `en-experiences` から取得した画像コンテンツ（id=スラグ）
 */
export const buildExperientialStayCardList = (experiences: WabunkaExperienceViewModel[], cmsImages: EnExperienceImageContent[]): ExperientialStayCardListViewModel => {
  const imageMap = buildImageMapBySlug(cmsImages);

  return experiences.reduce<ExperientialStayCardListViewModel>((cards, experience) => {
    const cms = imageMap.get(experience.slug);
    if (!cms) return cards;

    // PC スライドショーは pcMainImage + subImages を回す。SP は spMainImage 1 枚。
    const backgroundWideMedias = buildWideMedias(cms);
    const backgroundTallMedias = toMediaList(cms.spMainImage);
    // PC/SP いずれの画像も無いカードは非表示
    if (backgroundWideMedias.length === 0 && backgroundTallMedias.length === 0) return cards;

    // メタは他カード（ExperienceCardContent）と同一ロジックに揃える
    const destination = displayDestinationTagName(experience.tags);
    const duration = buildExperientialStayDuration(experience.experienceDuration);
    const price = displayPrice(experience) ?? '';

    cards.push({
      slug: experience.slug,
      titleEn: experience.name,
      meta: {
        destination,
        duration,
        price,
        hasMeta: Boolean(destination || duration || price),
      },
      detailLink: `/experiences/${experience.slug}/`,
      backgroundWideMedias,
      backgroundTallMedias,
    });
    return cards;
  }, []);
};
